import { useEffect, useMemo, useState } from 'react';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { fetchSmartlinkDataErrorEventPayload } from '../../../ai-answer-dialog/error-analytics';
import { fetchRestResponse } from '../../../common/fetch/fetch-rest-reponse';

export type ObjectResolverParamType = {
	resourceUrl: string;
};

export type ObjectResolverDataType = {
	body: ObjectResolverBody;
	status: number;
};

export type ObjectResolverBody = {
	meta: Meta;
	data: ObjectData;
	entityData?: ObjectEntityData;
};

export type ObjectData = {
	'@context': Context;
	generator?: Generator;
	'@type': string[];
	url: string;
	name: string;
	'atlassian:state': string;
	summary: string;
	updated: Date;
	'schema:dateCreated': Date;
	attributedTo: AtlassianOwnedBy;
	'schema:commentCount': number;
	'atlassian:reactCount': number;
	'atlassian:titlePrefix': AtlassianTitlePrefix;
	'atlassian:ownedBy': AtlassianOwnedBy;
	'atlassian:ari': string;
	preview: Preview;
};

export type Context = {
	'@vocab': string;
	atlassian: string;
	schema: string;
};

export type AtlassianOwnedBy = {
	'@type': string;
	name: string;
	icon: string;
};

export type AtlassianTitlePrefix = {
	text: string;
	'@type': string;
};

export type Generator = {
	'@type': string;
	'@id': string;
	name: string;
	icon: Icon;
};

export type Icon = {
	'@type': string;
	url: string;
};

export type Preview = {
	'@type': string;
	href: string;
	'atlassian:supportedPlatforms': string[];
};

export type ObjectEntityData = {
	type?: ObjectEntityDataType;
};

export type ObjectEntityDataType = {
	category: string;
};

export type Meta = {
	auth: any[];
	definitionId: string;
	product: string;
	visibility: string;
	access: string;
	resourceType: string;
	objectId: string;
	tenantId: string;
	activationId: string;
	containerId: string;
	category: string;
	supportedFeature: string[];
	key: string;
};

interface useFetchSmartlinkDataArgs {
	urls: string[];
}

const OBJECT_RESOLVER_ENDPOINT = '/gateway/api/object-resolver/resolve/batch';

export const useFetchSmartlinkData: ({ urls }: useFetchSmartlinkDataArgs) => {
	isLoading: boolean;
	objectResolverData: ObjectResolverBody[];
} = ({ urls }: useFetchSmartlinkDataArgs) => {
	const [isLoading, setIsLoading] = useState(true);
	const [objectResolverData, setObjectResolverData] = useState<ObjectResolverBody[]>([]);
	const { fireAnalyticsEvent, commonAttributes } = useAIAnswerContext();

	const urlKeys = useMemo(() => urls.join('|'), [urls]);

	useEffect(() => {
		const fetchObjectResolver = async () => {
			if (!urls.length) {
				setIsLoading(false);
				return;
			}

			try {
				const objectResolver = await fetchRestResponse<
					ObjectResolverParamType[],
					ObjectResolverDataType[]
				>({
					url: OBJECT_RESOLVER_ENDPOINT,
					body: urls.map((url) => ({ resourceUrl: url })),
				});

				if (!objectResolver) {
					setIsLoading(false);
					return;
				}

				const objectData = objectResolver.filter((obj) => obj.status === 200);
				setObjectResolverData(objectData.map((objData) => objData.body));
				setIsLoading(false);
			} catch (error) {
				if (error instanceof Object && 'message' in error) {
					fireAnalyticsEvent(fetchSmartlinkDataErrorEventPayload(error, commonAttributes));
				}

				setIsLoading(false);
			}
		};

		void fetchObjectResolver();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [urlKeys]);

	return {
		isLoading,
		objectResolverData,
	};
};
